import React, { FC, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, IconButton } from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { editInstitutions } from './action.ts';
import { validateRemarks } from '../../Utils/RemarksValidation/remarks-validation.tsx';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapModal from '../Warehouses/map-modal.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface EditInstitutionModalProps {
    open: boolean;
    onClose: () => void;
    institution: any;
}

const EditInstitutionModal: FC<EditInstitutionModalProps> = ({ open, onClose, institution }) => {
    const [institutionName, setInstitutionName] = useState('');
    const [institutionNameError, setInstitutionNameError] = useState<string>('');
    const [address, setAddress] = useState('');
    const [addressError, setAddressError] = useState<string>('');
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [latitudeError, setLatitudeError] = useState<string>('');
    const [longitudeError, setLongitudeError] = useState<string>('');
    const [mapModalOpen, setMapModalOpen] = useState(false);

    useEffect(() => {
        if (open && institution) {
            setInstitutionName(institution.institution_name || '');
            setAddress(institution.address || '');
            setLatitude(institution.latitude ? String(institution.latitude) : '');
            setLongitude(institution.longitude ? String(institution.longitude) : '');   
            setInstitutionNameError(''); 
            setAddressError('');
            setLatitudeError('');
            setLongitudeError('');
        }
    }, [open, institution]);

    const validateLatitude = (value: string): boolean => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= -90 && num <= 90;
    };

    const validateLongitude = (value: string): boolean => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= -180 && num <= 180;
    };

    const restrictedCharsRegex = /[%^*{}\[\]()]/;

    const handleInstitutionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setInstitutionName(value);
        
        if (value === '') {
            setInstitutionNameError('');
        } else if (validateRemarks(value)) {
            setInstitutionNameError('');
        } else {
            setInstitutionNameError('Invalid Character');
        }
    };

    const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setAddress(value);

        if (value.trim() === '') {
            setAddressError('Address is required');
        } else if (restrictedCharsRegex.test(value)) {
            setAddressError('Special characters like %^*{}[]() are not allowed');
        } else {
            setAddressError('');
        }
    };

    const handleLatitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLatitude(value);
        if (value === '') {
            setLatitudeError('');
        } else if (validateLatitude(value)) {
            setLatitudeError('');
        } else {
            setLatitudeError('Invalid latitude (-90 to 90)');
        }
    };

    const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLongitude(value);
        if (value === '') {
            setLongitudeError('');
        } else if (validateLongitude(value)) {
            setLongitudeError('');
        } else {
            setLongitudeError('Invalid longitude (-180 to 180)');
        }
    };

    const handleLocationSelect = (lat: string, lng: string) => {
        setLatitude(lat);
        setLongitude(lng);
        setLatitudeError('');
        setLongitudeError('');
        setMapModalOpen(false);
    };

    const handleGoogleMapsSelect = () => {
        setMapModalOpen(true);
    };

    const handleSave = () => {
        let hasError = false;

        if (
            institutionName === institution?.institution_name &&
            address === (institution?.address || '') &&
            latitude === (institution?.latitude ? String(institution.latitude) : '') &&
            longitude === (institution?.longitude ? String(institution.longitude) : '')
        ) {
            onClose();
            return;
        }
    
        if (!institutionName.trim()) {
            setInstitutionNameError('Institution Name is required');
            hasError = true;
        }
        
        if (!address.trim()) {
            setAddressError('Address is required');
            hasError = true;
        } else if (restrictedCharsRegex.test(address)) {
            setAddressError('Special characters like %^*{}[]() are not allowed');
            hasError = true;
        }

        if (!latitude) {
            setLatitudeError('Latitude is required');
            hasError = true;
        }
        
        if (!longitude) {
            setLongitudeError('Longitude is required');
            hasError = true;
        }
         
        if (hasError || institutionNameError || addressError || latitudeError || longitudeError) {
            toast.error("Please fix the errors");
            return;
        } else {
            editInstitutions(
                institution.id,
                institutionName.trim(),
                address.trim(),
                latitude.trim(),
                longitude.trim(),
                (response: any) => {
                    toast.success(response?.message || "Institution updated successfully");
                    setTimeout(() => {
                        window.location.reload();
                    }, 3000);
                },
                (errorMessage: any) => {
                    toast.error(errorMessage);
                }
            );

            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            maxWidth="xs" 
            sx={{
                '& .MuiDialog-paper': {
                    width: '400px',
                    maxWidth: 'none',
                },
            }}
        >
            <DialogTitle sx={{ padding: '0px' }}>
                <Box
                    sx={{
                        marginLeft: {
                            xs: '0px',
                            sm: '-180px',
                        },
                    }}
                >  
                    <Heading title="Edit Institution" closeIcon="true" onClose={onClose} />
                </Box>
            </DialogTitle>
            <DialogContent>
                <div style={{ marginTop: '5px' }}>
                    <p style={{ fontSize: '17px' }}>Institution Name</p>
                </div>
                <div style={{ marginLeft: '140px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='institution_name'
                            placeholder='Institution Name'
                            value={institutionName}
                            onChange={handleInstitutionNameChange}
                            error={!!institutionNameError}
                            helperText={institutionNameError}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </div>

                <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '17px' }}>Address</p>
                </div>
                <div style={{ marginLeft: '140px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='address'
                            placeholder='Full Address'
                            value={address}
                            onChange={handleAddressChange}
                            error={!!addressError}
                            helperText={addressError}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                        />
                    </Box>
                </div>

                <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '17px' }}>Latitude</p>
                </div>
                <div style={{ marginLeft: '100px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <IconButton
                            onClick={handleGoogleMapsSelect}
                            color="primary"
                            aria-label="select from map"
                            style={{ marginRight: '15px' }}
                        >
                            <LocationOnIcon />
                        </IconButton>
                        <TextField
                            size='small'
                            name='latitude'
                            placeholder='Latitude'
                            value={latitude}
                            onChange={handleLatitudeChange}
                            error={!!latitudeError}
                            helperText={latitudeError}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </div>

                <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '17px' }}>Longitude</p>
                </div>
                <div style={{ marginLeft: '140px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='longitude'
                            placeholder='Longitude'
                            value={longitude}
                            onChange={handleLongitudeChange}
                            error={!!longitudeError}
                            helperText={longitudeError}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </div>
            </DialogContent>
            
            <DialogActions style={{ justifyContent: 'center', paddingBottom: '20px' }}>
                <Button
                    onClick={onClose}
                    className="custom-cancel-button"
                    style={{ marginRight: '10px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    className="custom-button"
                    style={{ marginLeft: '10px' }}
                >
                    Save
                </Button>
            </DialogActions>

            <MapModal
                open={mapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onLocationSelect={handleLocationSelect}
                initialLocation={
                    latitude && longitude
                        ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
                        : undefined
                }
            />
        </Dialog>
    );
};

export default EditInstitutionModal;